import { useQuery } from '@apollo/client';
import { Error, OfflineBolt, Unpublished } from '@mui/icons-material';
import { Button, DialogActions, DialogContent, MenuItem, Select } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSendDeviceCommand } from '~/api/devices';
import { Tooltip } from '~/components/Tooltip';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { LoadingPane } from '~/components/loading-pane';
import { useAppContext } from '~/contexts';
import type { DeviceBulkSelectionInput } from '~/generated/graphql';
import { DeviceCommandKind } from '~/generated/graphql';
import { formatDate } from '~/lib/datetime';
import { UpdateSoftwareDialogDocument } from './UpdateSoftwareDialog.generated';

interface UpdateSoftwareDialogProps {
  onClose: () => void;
  onComplete?: () => void;
  selected: DeviceBulkSelectionInput;
}

export const UpdateSoftwareDialog = ({
  onClose,
  onComplete,
  selected,
}: UpdateSoftwareDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data, loading } = useQuery(UpdateSoftwareDialogDocument, {
    variables: { networkId: currentNetwork.id, selected },
  });
  const [version, setVersion] = useState('');
  // Each marker is the only sign of its status, so it names itself (WCAG 1.1.1).
  const eolTitle = (eoledAt: string | null | undefined) =>
    `${t('pages.devices.dialogs.update_software.eol_tooltip')} ${formatDate(eoledAt) ?? ''}`;
  const unapprovedTitle = t('pages.devices.dialogs.update_software.unapproved_tooltip');

  const [sendCommand] = useSendDeviceCommand();

  const updateSoftware = () => {
    void sendCommand({
      variables: { input: { command: DeviceCommandKind.Update, payload: { version }, selected } },
    });
    onComplete?.();
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open>
      <DialogTitle onClose={onClose}>
        {t('pages.devices.dialogs.update_software.title')}
      </DialogTitle>

      <DialogContent sx={{ fontSize: '1rem' }}>
        <LoadingPane in={loading && !data}>
          {data?.network && (
            <>
              {data.network.softwareUpdateInfo.currentVersion && (
                <DialogContentTitle>
                  {t('pages.devices.dialogs.update_software.current_version')}{' '}
                  <b>{data.network.softwareUpdateInfo.currentVersion}</b>.
                </DialogContentTitle>
              )}
              {data.network.softwareUpdateInfo.reason ? (
                <DialogContentTitle>{data.network.softwareUpdateInfo.reason}</DialogContentTitle>
              ) : (
                <Select<string>
                  displayEmpty
                  onChange={(event) => setVersion(event.target.value)}
                  sx={{ fontSize: 'inherit' }}
                  value={version}
                >
                  <MenuItem disabled value="">
                    {t('pages.devices.dialogs.update_software.select_version')}
                  </MenuItem>
                  {data.network.softwareUpdateInfo.versions.map((softwareVersion) => (
                    <MenuItem
                      disabled={
                        softwareVersion.version === data.network?.softwareUpdateInfo.currentVersion
                      }
                      key={softwareVersion.version}
                      value={softwareVersion.version}
                      sx={
                        softwareVersion.version === data.network?.softwareUpdateInfo.currentVersion
                          ? { backgroundColor: 'primary.main' }
                          : { color: 'inherit', textDecoration: 'none' }
                      }
                    >
                      {softwareVersion.version}
                      {softwareVersion.current === false && (
                        <Tooltip describeChild={false} title={eolTitle(softwareVersion.eoledAt)}>
                          <Error
                            titleAccess={eolTitle(softwareVersion.eoledAt)}
                            sx={{ color: 'error.main', marginLeft: '5px' }}
                          />
                        </Tooltip>
                      )}
                      {softwareVersion.approved === false && (
                        <Tooltip describeChild={false} title={unapprovedTitle}>
                          <Unpublished
                            titleAccess={unapprovedTitle}
                            sx={{ color: 'text.secondary', marginLeft: '5px' }}
                          />
                        </Tooltip>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </>
          )}
        </LoadingPane>
      </DialogContent>

      <DialogActions>
        <Button disabled={loading} onClick={onClose} variant="outlined">
          {t('common.actions.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={loading || version === ''}
          onClick={updateSoftware}
          startIcon={<OfflineBolt />}
          variant="contained"
        >
          {t('common.dialogs.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
